// frontend/src/pages/admin/settings.jsx
import React, { useState, useEffect } from 'react';
import { getSystemSettings, updateSystemSettings, checkLocationService } from 'api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import ResendStatus from '../../components/ResendStatus';

const SettingsPage = () => {
  const [settings, setSettings] = useState({
    leadNotificationFromEmail: '',
    // ... other settings
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [locationServiceStatus, setLocationServiceStatus] = useState({ status: 'checking', error: null });
  const [backupStats, setBackupStats] = useState({
    lastBackupDate: null,
    totalLeads: 0,
    validLeads: 0,
    placeholderLeads: 0
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchSettings();
    checkLocationServiceStatus();
    fetchBackupStats();
  }, []);

  // Fix the fetchBackupStats function in settings.jsx
const fetchBackupStats = async () => {
  try {
    // Use the correct API URL with authentication
    const token = localStorage.getItem('token');
    const response = await fetch('https://api.annuityleadsforagents.com/api/admin/backup-stats', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Server responded with status ${response.status}`);
    }
    
    const data = await response.json();
    setBackupStats(data);
  } catch (err) {
    console.error('Failed to fetch backup stats:', err);
    // Set default values instead of failing completely
    setBackupStats({
      lastBackupDate: null,
      totalLeads: 0,
      validLeads: 0,
      placeholderLeads: 0
    });
  }
};

  const checkLocationServiceStatus = async () => {
    try {
      const result = await checkLocationService();
      setLocationServiceStatus({
        status: result.status,
        error: result.error
      });
    } catch (err) {
      setLocationServiceStatus({
        status: 'offline',
        error: 'Failed to check service status'
      });
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      // Add error handling and logging
      console.log("Fetching system settings...");
      const result = await getSystemSettings();
      console.log("Settings response:", result);
      
      if (result.success) {
        setSettings(result.data);
      } else {
        setError(result.message || 'Failed to load settings');
        // Use default values as fallback
        setSettings({
          leadNotificationFromEmail: 'leads@annuityleadsforagents.com',
          // Other default settings
        });
      }
    } catch (err) {
      console.error('Settings fetch error details:', err);
      setError('Failed to load settings. Please try again.');
      // Use default values
      setSettings({
        leadNotificationFromEmail: 'leads@annuityleadsforagents.com',
        // Other default settings
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const result = await updateSystemSettings(settings);
      if (result.success) {
        setSuccessMessage('Settings updated successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.message || 'Failed to update settings');
      }
    } catch (err) {
      setError('Failed to update settings. Please try again.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-8">System Settings</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Backup Information Card */}
        <Card>
          <CardHeader>
            <CardTitle>Database Backup & Cleanup Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700">Lead Statistics</h3>
                <div className="mt-2 grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-500">Total Leads</p>
                    <p className="text-lg font-semibold">{backupStats.totalLeads}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-500">Valid Leads</p>
                    <p className="text-lg font-semibold">{backupStats.validLeads}</p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Backup Instructions</h3>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-700">
                    To create a backup of your leads database:
                  </p>
                  <ol className="mt-2 text-sm text-blue-600 list-decimal list-inside space-y-1">
                    <li>Connect to Railway using the CLI: <code className="bg-blue-100 px-1">railway connect</code></li>
                    <li>Run backup command: <code className="bg-blue-100 px-1">pg_dump $DATABASE_URL {'>'} backup_[date].sql</code></li>
                  </ol>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Placeholder Leads Cleanup</h3>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-sm text-yellow-700">
                    Before deleting placeholder leads:
                  </p>
                  <ol className="mt-2 text-sm text-yellow-600 list-decimal list-inside space-y-1">
                    <li>Create a backup table: <code className="bg-yellow-100 px-1">CREATE TABLE deleted_placeholder_leads AS SELECT * FROM leads WHERE email LIKE '%placeholder.unbounce%';</code></li>
                    <li>Verify the backup: <code className="bg-yellow-100 px-1">SELECT COUNT(*) FROM deleted_placeholder_leads;</code></li>
                    <li>Delete placeholder leads: <code className="bg-yellow-100 px-1">DELETE FROM leads WHERE email LIKE '%placeholder.unbounce%';</code></li>
                  </ol>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">CSV Import Template</h3>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-700 mb-2">
                    Download the CSV template for importing leads:
                  </p>
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={() => {
                        const headers = [
                          'name,email,phone,state,zip_code,age,investment_amount,annuity_type,term_length,income_start_time,retirement_timeline,primary_goal,financial_comfort_level,contact_preference,source,submitter_ip,created_at'
                        ];
                        const blob = new Blob([headers.join('\n')], { type: 'text/csv' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'leads_import_template.csv';
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      }}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      Download Template
                    </Button>
                    <span className="text-xs text-green-600">
                      Format: CSV with headers
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-green-600">
                    <p>Required fields:</p>
                    <ul className="list-disc list-inside mt-1">
                      <li>name: Full name of the lead</li>
                      <li>email: Valid email address</li>
                      <li>phone: Phone number (digits only)</li>
                      <li>state: Two-letter state code (e.g., FL, NY)</li>
                      <li>zip_code: 5-digit ZIP code</li>
                      <li>age: Numeric age</li>
                      <li>investment_amount: Numeric amount</li>
                      <li>source: Set to "annuity_answers"</li>
                      <li>contact_preference: Set to "email"</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm text-gray-500">
                  Last Database Backup: {backupStats.lastBackupDate 
                    ? new Date(backupStats.lastBackupDate).toLocaleDateString() 
                    : 'No recent backup'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle>Email Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <ResendStatus />
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {successMessage && (
                <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-green-700">{successMessage}</p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="leadNotificationFromEmail" className="block text-sm font-medium text-gray-700">
                  Lead Notification From Email
                </label>
                <div className="mt-1">
                  <input
                    type="email"
                    name="leadNotificationFromEmail"
                    id="leadNotificationFromEmail"
                    value={settings.leadNotificationFromEmail}
                    onChange={handleChange}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="leads@yourdomain.com"
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  This email address will be used as the sender for all lead notification emails.
                  Default: leads@annuityleadsforagents.com
                </p>
              </div>

              {/* ... other settings fields ... */}

              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={saving}
                  className="ml-3"
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Location Service Card */}
        <Card>
          <CardHeader>
            <CardTitle>Location Service</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${
                locationServiceStatus.status === 'online' ? 'bg-green-100' : 'bg-red-100'
              }`}></div>
              <span className="font-medium">
                {locationServiceStatus.status === 'online' ? 'Online' : 'Offline'}
              </span>
            </div>
            {locationServiceStatus.error && (
              <p className="text-sm text-red-600 mt-2">{locationServiceStatus.error}</p>
            )}
          </CardContent>
        </Card>


{/* State Update Tool Card */}
<Card>
  <CardHeader>
    <CardTitle>Lead State Update Tool</CardTitle>
  </CardHeader>
  <CardContent>
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        This tool will help you update missing state information for leads based on their ZIP codes.
      </p>
      
      <div className="bg-blue-50 p-4 rounded-lg">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Bulk Update All Missing States</h3>
        <p className="text-sm text-blue-700 mb-4">
          Click the button below to automatically update all leads that have ZIP codes but missing state information.
        </p>
        <Button
          onClick={async () => {
            try {
              const token = localStorage.getItem('token');
              setIsProcessing(true);
              setErrorMessage('');
              setStatusMessage('');
              
              const response = await fetch('https://api.annuityleadsforagents.com/api/admin/leads/auto-update-states', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
              
              const result = await response.json();
              if (result.success) {
                setStatusMessage(result.message);
              } else {
                setErrorMessage(result.message);
              }
            } catch (err) {
              setErrorMessage('Error: ' + err.message);
            } finally {
              setIsProcessing(false);
            }
          }}
          disabled={isProcessing}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {isProcessing ? 'Processing...' : 'Auto-Update All Missing States'}
        </Button>
        
        {statusMessage && (
          <p className="mt-2 text-sm text-green-700">{statusMessage}</p>
        )}
        {errorMessage && (
          <p className="mt-2 text-sm text-red-700">{errorMessage}</p>
        )}
      </div>
      
      <div className="bg-green-50 p-4 rounded-lg">
        <h3 className="text-sm font-medium text-green-800 mb-2">Automatic State Assignment</h3>
        <p className="text-sm text-green-700 mb-2">
          When this setting is enabled, the system will automatically determine the state based on the ZIP code
          whenever a new lead is created or a lead's ZIP code is updated.
        </p>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={settings.autoAssignStateFromZip || false}
            onChange={(e) => setSettings(prev => ({
              ...prev,
              autoAssignStateFromZip: e.target.checked
            }))}
            className="rounded text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Enable automatic state assignment</span>
        </label>
      </div>
    </div>
  </CardContent>
</Card>

      </div>
    </div>
  );
};

export default SettingsPage;